import logger, { toErrorMessage } from '@/helpers/logger'
import { versionDtoSchema, type VersionDto } from '@/schemas/version.schema'

/**
 * The deployed SHA the last successful poll saw, or null before the first one.
 *
 * Kept at module scope because the question "is this tab out of date" is asked
 * outside React — when a response fails its schema, which is what an out-of-date
 * tab looks like from the inside.
 */
let lastDeployedSha: string | null = null

/**
 * True when this tab is knowingly running older JS than what is deployed.
 *
 * `false` before the first poll answers, and `false` when they match — so it is
 * only ever used to explain a failure, never to assume one. Deliberately not
 * "might be out of date": a tab that has not checked yet is treated as current,
 * because guessing the other way would quietly excuse real faults.
 */
export function appIsOutdated(): boolean {
  return lastDeployedSha !== null && lastDeployedSha !== __APP_VERSION_SHA__
}

/**
 * Fetch the currently deployed web app version (`GET /version.json` on this
 * origin). Compared to the build-inlined `__APP_VERSION_SHA__` to detect a
 * newer deploy. Uses cache: no-store so a CDN does not hide an update.
 * Returns null when unavailable (network error / invalid payload).
 */
export async function fetchDeployedVersion(): Promise<VersionDto | null> {
  try {
    const response = await fetch('/version.json', { cache: 'no-store' })
    if (!response.ok) {
      return null
    }
    const text = await response.text()
    if (text.trim() === '') {
      return null
    }
    // JSON.parse is `any` in lib.dom; safeParse is the typed boundary.
    const result = versionDtoSchema.safeParse(JSON.parse(text))
    if (!result.success) {
      logger.warn('version.json failed schema validation', {
        issues: result.error.issues.map((i) => i.message).join('; '),
      })
      return null
    }
    lastDeployedSha = result.data.sha
    return result.data
  } catch (error) {
    logger.warn('Failed to fetch version.json', { error: toErrorMessage(error) })
    return null
  }
}
